import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { serverTimestamp } from 'firebase/firestore';
import { auth, googleProvider } from 'config/firebase';
import { generateGymSlug } from 'utils/slugify';
import { isLegacyAdminEmail } from 'constants/adminEmails';
// Import direction rule (Standards.md §2.1): this context deep-imports feature
// service modules ONLY — never feature barrels (barrels re-export components
// that call useAuth, which would create an import cycle).
import * as userProfileService from 'features/auth/services/userProfileService';
import * as gymsService from 'features/gyms/services/gymsService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userGym, setUserGym] = useState(null); // gym doc for gym_owner users
  const [userGymId, setUserGymId] = useState(null); // doc ID of the gym
  const [loading, setLoading] = useState(true);

  // Fetch the gym doc for a gym_owner user
  const fetchGymForUser = async (uid, profile) => {
    try {
      const result = await gymsService.getGymForOwner(uid, profile);
      setUserGym(result ? result.gym : null);
      setUserGymId(result ? result.gymId : null);
    } catch (err) {
      console.warn('Could not fetch gym for user:', err.code);
      setUserGym(null);
      setUserGymId(null);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        try {
          // Retry transient failures: a single flaky read here used to leave
          // userProfile null for the whole session, silently downgrading an
          // admin/owner to student — every role-gated route then bounced them
          // (e.g. clicking Edit on their own event) with no visible error.
          let profile = null;
          for (let attempt = 0; attempt < 3; attempt++) {
            try {
              profile = await userProfileService.getUserProfile(firebaseUser.uid);
              break;
            } catch (err) {
              if (attempt === 2) throw err;
              await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
            }
          }
          if (profile) {
            // One-time bootstrap: promote legacy admin emails to role:'admin' in Firestore
            // so Storage/Firestore rules can rely on the role field rather than hardcoded emails.
            if (isLegacyAdminEmail(firebaseUser.email) && profile.role !== 'admin') {
              await userProfileService.promoteToAdmin(firebaseUser.uid);
              profile = { ...profile, role: 'admin' };
            }
            setUserProfile(profile);
            // Fetch gym doc if this is a gym owner (admins/coaches also load their gym)
            const isAdminUser = profile.role === 'admin' || isLegacyAdminEmail(firebaseUser.email);
            if (profile.role === 'gym_owner' || profile.role === 'coach' || isAdminUser) {
              await fetchGymForUser(firebaseUser.uid, profile);
            }
          }
        } catch (err) {
          console.error('Could not fetch user profile:', err.code, err.message);
        }
      } else {
        setUser(null);
        setUserProfile(null);
        setUserGym(null);
        setUserGymId(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const ensureUserProfile = async (firebaseUser, role, displayName) => {
    try {
      const existing = await userProfileService.getUserProfile(firebaseUser.uid);
      if (existing) {
        setUserProfile(existing);
        return existing;
      }
      const profile = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: displayName || firebaseUser.displayName || '',
        role: role,
        photoURL: firebaseUser.photoURL || '',
        createdAt: serverTimestamp(),
        ...(role === 'gym_owner' && {
          gymIds: [],
          gymSetupComplete: false,
        }),
        ...(role === 'coach' && {
          coachBio: '',
          coachDisciplines: [],
          coachWebsite: '',
          coachInstagram: '',
          coachSetupComplete: false,
          coachSettings: {
            defaultPrice: null,
            defaultCurrency: 'EUR',
            defaultDuration: null,
            defaultMaxSpots: null,
            cancellationPolicy: '',
            notifyOnBooking: true,
            schedule: [],
            logoUrl: '',
          },
        }),
      };
      await userProfileService.createUserProfile(profile);
      setUserProfile(profile);
      return profile;
    } catch (err) {
      console.error('ensureUserProfile error:', err.code);
      // Return a local profile so the UI can still navigate correctly
      const fallback = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: displayName || firebaseUser.displayName || '',
        role: role,
        photoURL: firebaseUser.photoURL || '',
      };
      setUserProfile(fallback);
      return fallback;
    }
  };

  const signInWithGoogle = async (role = 'student') => {
    const result = await signInWithPopup(auth, googleProvider);
    const profile = await ensureUserProfile(result.user, role);
    return { user: result.user, profile };
  };

  const signUpWithEmail = async (email, password, displayName, role = 'student') => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(result.user, { displayName });
    const continuePath = role === 'gym_owner' ? '/gym-dashboard' : '/account';
    await sendEmailVerification(result.user, {
      url: `${window.location.origin}${continuePath}`,
      handleCodeInApp: false,
    });
    const profile = await ensureUserProfile(result.user, role, displayName);
    return { user: result.user, profile };
  };

  const signInWithEmail = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
    setUser(null);
    setUserProfile(null);
  };

  const updateUserProfile = async (data) => {
    if (!user) return;
    await userProfileService.updateUserProfile(user.uid, data);
    setUserProfile((prev) => ({ ...prev, ...data }));
  };

  // Create or update a gym doc in the gyms collection
  const updateUserGym = async (gymData) => {
    if (!user) return null;
    if (userGymId) {
      // Update existing gym doc — refresh slug if name/city/country changed and no slug yet
      const merged = { ...userGym, ...gymData };
      const patch = { ...gymData, updatedAt: serverTimestamp() };
      // Resubmitting a rejected gym sends it back to the review queue. (Owners
      // can't self-approve — only this rejected→pending hop is allowed by rules.)
      if (userGym?.approvalStatus === 'rejected') {
        patch.approvalStatus = 'pending';
      }
      if (!merged.slug && merged.name) {
        const baseSlug = generateGymSlug({
          name: merged.name,
          city: merged.city,
          country: merged.country,
        });
        if (baseSlug) patch.slug = await gymsService.ensureUniqueGymSlug(baseSlug, userGymId);
      }
      await gymsService.updateGym(userGymId, patch);
      setUserGym((prev) => ({ ...prev, ...patch }));
      return userGymId;
    } else {
      const baseSlug = generateGymSlug({
        name: gymData.name,
        city: gymData.city,
        country: gymData.country,
      });
      const slug = baseSlug ? await gymsService.ensureUniqueGymSlug(baseSlug) : '';
      // Self-created gyms must be reviewed by an admin before their events can
      // go live (anti-scam safeguard). Admins are trusted and skip the queue —
      // their gyms (e.g. the legacy "OpenMat Admin" bootstrap gym) are approved
      // on creation. Everyone else starts 'pending' and sees the review banner.
      const isAdminUser = userProfile?.role === 'admin' || isLegacyAdminEmail(user.email);
      const newGymData = {
        ...gymData,
        slug,
        ownerUserIds: [user.uid],
        isClaimed: true,
        approvalStatus: isAdminUser ? 'approved' : 'pending',
        source: 'user_created',
        createdByUid: user.uid,
      };
      // createGym stamps createdAt/updatedAt server-side timestamps.
      const gymId = await gymsService.createGym(newGymData);
      await userProfileService.addGymToProfile(user.uid, gymId);
      setUserProfile((prev) => ({ ...prev, gymIds: [...(prev?.gymIds || []), gymId] }));
      setUserGym(newGymData);
      setUserGymId(gymId);
      return gymId;
    }
  };

  const resetPassword = async (email) => {
    await sendPasswordResetEmail(auth, email, {
      url: `${window.location.origin}/login`,
      handleCodeInApp: false,
    });
  };

  // Re-send the account-verification email to the currently signed-in user.
  // Used by the /verify-email gate when someone didn't get (or lost) the original.
  const resendVerificationEmail = async () => {
    if (!auth.currentUser) return;
    const role = userProfile?.role;
    const continuePath = role === 'gym_owner' || role === 'coach' ? '/gym-dashboard' : '/account';
    await sendEmailVerification(auth.currentUser, {
      url: `${window.location.origin}${continuePath}`,
      handleCodeInApp: false,
    });
  };

  const toggleSavedEvent = async (eventId) => {
    if (!user) return;
    const isSaved = (userProfile?.savedEvents || []).includes(eventId);
    const next = isSaved
      ? (userProfile?.savedEvents || []).filter((id) => id !== eventId)
      : [...(userProfile?.savedEvents || []), eventId];
    // Optimistic local update
    setUserProfile((prev) => ({ ...prev, savedEvents: next }));
    try {
      await userProfileService.setEventSaved(user.uid, eventId, !isSaved);
    } catch {
      // Rollback
      setUserProfile((prev) => ({ ...prev, savedEvents: userProfile?.savedEvents || [] }));
    }
  };

  // Gym approval state. A gym with no approvalStatus field predates the review
  // flow and is treated as approved (grandfathered). `gymApprovalStatus` is null
  // when the owner hasn't created a gym yet.
  const isAdminUser = userProfile?.role === 'admin' || isLegacyAdminEmail(user?.email);
  const isCoachUser = userProfile?.role === 'coach';
  const gymApprovalStatus = userGym ? userGym.approvalStatus || 'approved' : null;
  const gymPending = gymApprovalStatus === 'pending';
  const gymRejected = gymApprovalStatus === 'rejected';
  // Who is allowed to post events: admins and coaches always; gym owners only
  // once their gym exists and has been approved. Mirrors the Firestore rules.
  const canPostEvents = isAdminUser || isCoachUser || gymApprovalStatus === 'approved';

  const value = {
    user,
    userProfile,
    userGym,
    userGymId,
    loading,
    gymApprovalStatus,
    gymPending,
    gymRejected,
    canPostEvents,
    signInWithGoogle,
    signUpWithEmail,
    signInWithEmail,
    signOut,
    updateUserProfile,
    updateUserGym,
    resetPassword,
    resendVerificationEmail,
    isGymOwner: userProfile?.role === 'gym_owner',
    isCoach: userProfile?.role === 'coach',
    isStudent: userProfile?.role === 'student',
    isAdmin: userProfile?.role === 'admin' || isLegacyAdminEmail(user?.email),
    canManageGym: userProfile?.role === 'gym_owner',
    canPublishEvents:
      isAdminUser || userProfile?.role === 'gym_owner' || userProfile?.role === 'coach',
    savedEvents: userProfile?.savedEvents || [],
    toggleSavedEvent,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
